import { FC } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import { ProductionSceneImageResource } from "@/types/resources";
import { Modal } from "@/components/Modal";
import {
  StyledDialog,
  CloseButton,
  Actions,
} from "@/components/AlertDialogs/Dialog";
import Text from "@/components/Text";
import FormField from "@/components/Forms/FormField";
import Button from "@/components/Button";
import { FormLayout } from "@/components/Forms";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

const StyledFormLayout = styled(FormLayout)`
  gap: 16px;
`;

const Dialog = styled(StyledDialog)`
  width: 440px;
`;

interface FormDataT {
  description: string;
  image: string;
}

interface Props {
  onClose: () => void;
  editableSceneImage?: ProductionSceneImageResource;
  sceneId?: number;
}

const ImageFormModal: FC<Props> = ({
  onClose,
  editableSceneImage,
  sceneId,
}) => {
  const alerts = useAlerts();

  const { projectId } = useParams();

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      description: editableSceneImage?.description ?? "",
      image: "",
    },
  });

  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const body = {
        ...data,
        production_scene_id: sceneId,
      };

      if (editableSceneImage) {
        await api.put<typeof body, { id: number }>(
          `/v1/production/scenes/images/${editableSceneImage.id}`,
          body
        );
      } else {
        await api.post<typeof body, { id: number }>(
          "/v1/production/scenes/images",
          body
        );
      }

      await queryClient.invalidateQueries([
        "production_projects",
        { projectId },
      ]);

      onClose();

      alerts.success(
        `Image has been successfully ${
          editableSceneImage ? "updated" : "created"
        }`
      );
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose} type="button">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">
          {editableSceneImage ? "Edit" : "Add"} image and description
        </Heading>
        <form autoComplete="off" onSubmit={onSubmit}>
          <StyledFormLayout>
            <FormField<FormDataT>
              type="image"
              name="image"
              id="scene-image-input"
              control={control}
              existingMedia={editableSceneImage?.image}
            />
            <FormField<FormDataT>
              name="description"
              type="textarea"
              control={control}
              label="Description"
            />
          </StyledFormLayout>
          <Actions>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting || !isDirty}
              type="submit"
            >
              Save
            </Button>
            <Button onClick={onClose} color="secondary" type="button">
              Cancel
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Modal>
  );
};

export default ImageFormModal;
